import { useState, useEffect, useContext, ReactNode, useRef } from 'react';
import { TransactionValidationDialog } from '../components/shared/dialogs/TransactionValidationDialog';
import { TransactionValidationResponse, TransactionValidationConflict } from '../app/services/api/transactionValidationTypes';
import { TransactionValidationMode } from '../enums/TransactionValidationMode';
import { ConfigurationName } from '../enums/ConfigurationName';
import { UserContext } from '../components/shared/useUser';
import useNswagClient from './api/useNswagClient';
import useLogError from './useLogError';

export const useTransactionValidationGuard = () => {
  const { selectedStore } = useContext(UserContext);
  const { getConfiguration } = useNswagClient();
  const { logError } = useLogError();
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<'Warn' | 'Block'>('Warn');
  const [conflicts, setConflicts] = useState<TransactionValidationConflict[]>([]);
  const [pendingProceed, setPendingProceed] = useState<(() => void) | null>(null);
  const [isGuardEnabled, setIsGuardEnabled] = useState<boolean | null>(null);
  const isValidationInFlight = useRef(false);
  const validationGeneration = useRef(0);
  const currentStoreNumber = useRef(selectedStore?.storeNumber);
  currentStoreNumber.current = selectedStore?.storeNumber;

  useEffect(() => {
    let isCurrent = true;
    const storeGeneration = ++validationGeneration.current;
    setOpen(false);
    setPendingProceed(null);
    setIsGuardEnabled(null);
    isValidationInFlight.current = false;
    if (!selectedStore?.storeNumber) {
      return;
    }
    getConfiguration(ConfigurationName.TransactionValidation, selectedStore.storeNumber, selectedStore.franchiseName ?? '')
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .then((res: any) => {
        if (!isCurrent || validationGeneration.current !== storeGeneration) {
          return;
        }
        const value = (res?.data?.value ?? '').toString().toLowerCase();
        setIsGuardEnabled(value === TransactionValidationMode.Warn.toLowerCase() || value === TransactionValidationMode.Block.toLowerCase());
      })
      .catch((error) => {
        if (!isCurrent || validationGeneration.current !== storeGeneration) {
          return;
        }
        logError(error);
        setIsGuardEnabled(false);
      });
    return () => {
      isCurrent = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStore?.storeNumber]);

  const runGuard = async (check: () => Promise<TransactionValidationResponse>, onProceed: () => void) => {
    if (isValidationInFlight.current) {
      return;
    }

    if (isGuardEnabled === false || !selectedStore?.storeNumber) {
      onProceed();
      return;
    }
    const storeNumber = selectedStore.storeNumber;
    const requestGeneration = validationGeneration.current;
    isValidationInFlight.current = true;
    const keepLockForDialog = { current: false };
    try {
      const result = (await check())?.data;

      if (currentStoreNumber.current !== storeNumber || validationGeneration.current !== requestGeneration) {
        return;
      }

      if (result?.shouldBlock) {
        keepLockForDialog.current = true;
        setMode('Block');
        setConflicts(result.conflicts ?? []);
        setPendingProceed(null);
        setOpen(true);
        return;
      }

      if (result?.shouldWarn) {
        keepLockForDialog.current = true;
        setMode('Warn');
        setConflicts(result.conflicts ?? []);
        setPendingProceed(() => onProceed);
        setOpen(true);
        return;
      }

      onProceed();
    } catch (error) {
      if (currentStoreNumber.current === storeNumber) {
        logError(error);
        onProceed();
      }
    } finally {
      if (!keepLockForDialog.current && validationGeneration.current === requestGeneration) {
        isValidationInFlight.current = false;
      }
    }
  };

  const handleContinue = () => {
    setOpen(false);
    isValidationInFlight.current = false;
    const proceed = pendingProceed;
    setPendingProceed(null);
    if (proceed) {
      proceed();
    }
  };

  const handleCancel = () => {
    setOpen(false);
    isValidationInFlight.current = false;
    setPendingProceed(null);
  };

  const transactionValidationDialog: ReactNode = (
    <TransactionValidationDialog
      open={open}
      mode={mode}
      conflicts={conflicts}
      onContinue={handleContinue}
      onCancel={handleCancel}
    />
  );

  return { runGuard, transactionValidationDialog };
};
